/**
 * Maps Supabase auth errors and our own error strings to clear, user-friendly messages.
 * Use a single mapper everywhere so the copy is consistent across all auth surfaces.
 */
export type AuthFlow = "signin" | "signup" | "forgot" | "reset" | "resend";

export function friendlyAuthError(raw: string | undefined | null, flow: AuthFlow): string {
  if (!raw) {
    return flow === "signin"
      ? "We couldn't sign you in. Please try again."
      : "Something went wrong. Please try again.";
  }
  const msg = raw.toLowerCase();

  // Rate limit / "try again in N seconds" - keep verbatim, it's already useful
  if (
    msg.includes("for security purposes") ||
    msg.includes("over_email_send_rate_limit") ||
    (msg.includes("after") && msg.includes("seconds"))
  ) {
    return raw;
  }
  if (msg.includes("rate") || msg.includes("too many")) {
    return "Too many attempts. Please wait a moment and try again.";
  }

  // Password strength / HIBP - keep tone calm and neutral, never imply the user was breached
  if (msg.includes("weak") || msg.includes("pwned") || msg.includes("known to be") || msg.includes("breach")) {
    return "This password is too common or has been used before. Please choose a more unique one.";
  }
  if (msg.includes("password") && (msg.includes("short") || msg.includes("length") || msg.includes("at least"))) {
    return "Password must be at least 8 characters.";
  }

  // Wrong creds
  if (msg.includes("invalid login") || msg.includes("invalid_credentials") || msg.includes("invalid email or password")) {
    return "Email or password is incorrect.";
  }

  // Email not confirmed
  if (msg.includes("email not confirmed") || msg.includes("email_not_confirmed")) {
    return "Please confirm your email first - check your inbox for the verification link.";
  }

  // Already exists
  if (msg.includes("already registered") || msg.includes("already exists") || msg.includes("user already")) {
    return "An account with this email already exists. Try signing in instead.";
  }

  // Email format
  if (msg.includes("email") && msg.includes("invalid")) {
    return "That email address doesn't look right. Please check and try again.";
  }

  // Network
  if (msg.includes("network") || msg.includes("failed to fetch")) {
    return "Network error. Check your connection and try again.";
  }

  // Fallback
  return raw;
}

export function isValidEmail(email: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());
}

/**
 * Returns true when the given user-facing message is the soft "password is too common"
 * notice. Callers can use this to render a calm amber notice instead of a red error.
 */
export function isLeakedPasswordNotice(message: string | null | undefined): boolean {
  if (!message) return false;
  const m = message.toLowerCase();
  return m.includes("too common") || m.includes("used before") || m.includes("hasn't been used elsewhere");
}

export interface PasswordChecks {
  minLength: boolean;
  hasLetter: boolean;
  hasNumber: boolean;
}

export function checkPassword(password: string): PasswordChecks {
  return {
    minLength: password.length >= 8,
    hasLetter: /[a-zA-Z]/.test(password),
    hasNumber: /\d/.test(password),
  };
}

export function isPasswordValid(password: string): boolean {
  const c = checkPassword(password);
  return c.minLength && c.hasLetter && c.hasNumber;
}
